import { useEffect, useMemo, useRef, useState } from "react"
import { NavLink } from "react-router-dom"
import { Search } from "lucide-react"
import { api } from "../../api/client"
import { useApp } from "../../state/AppState"
import { publishIssues } from "../../state/issueFeed"
import { cx } from "../ui"

/**
 * The second sidebar: every issue in the open project, one click from any
 * other, so working through a list does not mean going back to it each time.
 *
 * It refetches whenever the project's summary changes - every mutation calls
 * `refreshProjects` - so an issue raised or resolved on the page shows here
 * without the two sharing state.
 *
 * The dot is the status at a glance: green resolved, amber open, red while you
 * have been asked to respond. Replying clears your assignment, so it returns
 * to amber.
 */
export function IssueRail({ projectId, activeIssueId }) {
	const { projects, user } = useApp()
	const [issues, setIssues] = useState([])
	const [query, setQuery] = useState("")
	const activeRef = useRef(null)

	const summary = projects.find((project) => project.project_id === projectId)
	const version = summary
		? [summary.updated_at, summary.last_activity_at, summary.open_count, summary.issue_count].join("|")
		: ""

	useEffect(() => {
		let cancelled = false
		api
			.issues(projectId)
			.then((data) => {
				if (cancelled) return
				setIssues(data.issues || [])
				publishIssues(projectId, data.issues || [])
			})
			.catch(() => {
				// The page itself reports a project it cannot load; the rail stays quiet.
				if (!cancelled) setIssues([])
			})
		return () => {
			cancelled = true
		}
	}, [projectId, version])

	// Keep the selected issue in view when arriving from elsewhere.
	useEffect(() => {
		activeRef.current?.scrollIntoView({ block: "nearest" })
	}, [activeIssueId, issues.length])

	const groups = useMemo(() => {
		const needle = query.trim().toLowerCase()
		const matching = issues
			.filter(
				(issue) =>
					!needle ||
					issue.title.toLowerCase().includes(needle) ||
					String(issue.number) === needle.replace(/^#/, "")
			)
			.sort((a, b) => a.number - b.number)
		return [
			{ key: "open", label: "Open", rows: matching.filter((i) => i.status === "open") },
			{ key: "closed", label: "Resolved", rows: matching.filter((i) => i.status !== "open") }
		].filter((group) => group.rows.length > 0)
	}, [issues, query])

	return (
		<aside
			aria-label="Issues in this project"
			className="sticky top-0 z-10 hidden h-screen w-72 shrink-0 flex-col border-r border-ink-200 bg-white shadow-[4px_0_16px_-8px_rgba(15,23,42,0.15)] lg:flex"
		>
			<div className="border-b border-ink-100 px-3 pb-3 pt-4">
				<p className="mb-2 truncate px-1 text-2xs font-semibold uppercase tracking-wider text-ink-400">
					{summary?.name || "Issues"}
				</p>
				<label className="relative block">
					<Search
						size={13}
						className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400"
						aria-hidden
					/>
					<input
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Jump to an issue"
						aria-label="Filter issues"
						className="h-8 w-full rounded-md border border-ink-200 bg-white pl-7 pr-2 text-[0.8125rem] text-ink-900 placeholder:text-ink-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25"
					/>
				</label>
			</div>

			<nav className="flex-1 overflow-y-auto px-2 py-2 scrollbar-slim">
				{groups.map((group) => (
					<div key={group.key} className="mb-3">
						<p className="px-2 pb-1 pt-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-400">
							{group.label} · {group.rows.length}
						</p>
						<ul className="space-y-0.5">
							{group.rows.map((issue) => {
								const active = issue.issue_id === activeIssueId
								const closed = issue.status !== "open"
								const waitingOnMe = !closed && issue.assignee_id === user?.user_id
								return (
									<li key={issue.issue_id} ref={active ? activeRef : undefined}>
										<NavLink
											to={`/projects/${projectId}/issues/${issue.issue_id}`}
											aria-current={active ? "page" : undefined}
											className={cx(
												"flex gap-2 rounded-md border px-2 py-1.5 text-[0.8125rem] leading-snug",
												active
													? "border-brand-600 bg-brand-50 font-medium text-brand-900"
													: "border-transparent text-ink-700 hover:border-brand-600 hover:text-ink-900"
											)}
										>
											<span
												className={cx(
													"mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full",
													closed
														? "bg-state-closed"
														: waitingOnMe
															? "bg-rose-600"
															: "bg-amber-500"
												)}
												title={closed ? "Resolved" : waitingOnMe ? "Waiting on you" : "Open"}
											/>
											<span className="shrink-0 tabular text-ink-400">#{issue.number}</span>
											<span className="line-clamp-2 min-w-0">{issue.title}</span>
										</NavLink>
									</li>
								)
							})}
						</ul>
					</div>
				))}
				{groups.length === 0 && (
					<p className="px-2 py-3 text-2xs leading-relaxed text-ink-500">
						{issues.length ? "No issue matches that." : "No issues in this project yet."}
					</p>
				)}
			</nav>
		</aside>
	)
}
